// Wires the form submission window-hooks (window.onSampleReportSubmit,
// window.onContactSubmit, window.onEarlyAccessSubmit) to the marketing-only
// API namespace served by Cloudflare Pages Functions (functions/api/marketing
// → D1). Exact legacy /api endpoints remain available for external clients.
//
// Imported once as a side-effect from Forms.jsx, which is loaded by every page
// (homepage, product page, and every sub-page via ModalRoot).

import posthog, { getDistinctId, isInternalDevice } from '../src/posthog.js';
import { hasGlobalPrivacyControl } from './consent.js';

// API base URL resolution:
//   - Production: same-origin (empty base) — Pages Functions are co-located.
//   - `npm run dev` (vite :5173): same-origin too, because vite.config.js
//     proxies `/api/*` to `http://localhost:8788` (wrangler pages dev).
//   - Override at build time with `VITE_API_BASE_URL=http://localhost:8788`
//     to call wrangler directly (bypassing the proxy). The API would then
//     need CORS headers — keep the proxy by default.
const API_BASE = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE_URL) || '';

async function postJson(path, payload) {
  const distinctId = getDistinctId();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(distinctId ? { 'x-posthog-distinct-id': distinctId } : {}),
      // A marked device has no posthog client, so it sends no distinct ID and
      // the server would fall back to a hashed email and record the lead event
      // anyway. This header is what actually suppresses it.
      ...(isInternalDevice() ? { 'x-scopeworth-internal': '1' } : {}),
      ...(hasGlobalPrivacyControl() ? { 'x-scopeworth-gpc': '1' } : {}),
    },
    body: JSON.stringify(payload || {}),
    credentials: 'same-origin',
  });
  let body = null;
  try { body = await res.json(); } catch (_) { /* non-JSON response */ }
  if (!res.ok) {
    const err = new Error((body && body.error) || `HTTP ${res.status}`);
    err.status = res.status;
    err.fields = (body && body.fields) || null;
    throw err;
  }
  const result = body || { ok: true };

  // Only the sample-report flow has an explicit contact-consent choice. On
  // that path, merge the anonymous browser journey into the submitted email
  // profile after the backend has accepted the row. Other forms still emit
  // non-PII conversion events but do not identify a person in the browser.
  if (
    result && result.ok !== false &&
    payload && payload.contactConsent === true &&
    posthog.__loaded && typeof posthog.identify === 'function'
  ) {
    const email = typeof payload.workEmail === 'string' ? payload.workEmail.trim() : '';
    if (email) {
      try {
        posthog.identify(email, {
          email,
          name: payload.fullName || undefined,
          company_name: payload.companyName || undefined,
        });
      } catch (_) { /* analytics never blocks the form */ }
    }
  }

  return result;
}

// Fetches the rendered sample-report HTML from
// /api/marketing/sample-report/render
// and hands the raw HTML text back to the caller. Used by SuccessView in
// two ways:
//   • Drop into a hidden iframe and call iframe.contentWindow.print() →
//     "Save as PDF" dialog (the Download PDF button).
//   • Wrap in a Blob URL and window.open() → user reads the report in a
//     new tab (the View Report button).
// Decoupled from /api/marketing/sample-report — that endpoint persists to D1.
async function fetchSampleReportHtml(payload) {
  const res = await fetch(`${API_BASE}/api/marketing/sample-report/render`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload || {}),
    credentials: 'same-origin',
  });
  if (!res.ok) {
    let body = null;
    try { body = await res.json(); } catch (_) {}
    const err = new Error((body && body.error) || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return await res.text();
}

// Looks up the most recent sample-report submission for `email`. Returns
// { exists: false } when none, or
// { exists: true, fullName, companyName, createdAt, payload } when found.
// Used by OnboardingApp to offer a returning user the choice between
// redownloading their previous report or starting fresh.
async function lookupSampleReport(email) {
  const e = String(email || '').trim();
  if (!e) return { exists: false };
  const res = await fetch(`${API_BASE}/api/marketing/sample-report?email=${encodeURIComponent(e)}`, {
    method: 'GET',
    headers: { 'accept': 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) {
    // Treat any non-2xx as "no match" so a flaky lookup never blocks the
    // onboarding form. The submit path still validates server-side.
    return { exists: false };
  }
  try {
    return await res.json();
  } catch (_) {
    return { exists: false };
  }
}

if (typeof window !== 'undefined') {
  window.onSampleReportSubmit = (payload) => postJson('/api/marketing/sample-report', payload);
  window.onContactSubmit      = (payload) => postJson('/api/marketing/contact', payload);
  window.onEarlyAccessSubmit  = (payload) => postJson('/api/marketing/early-access', payload);
  window.fetchSampleReportHtml = fetchSampleReportHtml;
  window.lookupSampleReport    = lookupSampleReport;
}
